<!-- This is component for tiptap text area with bubble menu for inline link and bullet list -->
<script setup>
import { ref, watch, computed, onBeforeUnmount } from "vue";
import TooltipIcon from "./TooltipIcon.vue";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { Editor, EditorContent } from "@tiptap/vue-3";
import { BubbleMenu } from "@tiptap/vue-3/menus";

defineOptions({ inheritAttrs: false });

const props = defineProps({
    modelValue: null,
    required: Boolean,
    errors: { type: Object, default: () => ({}) },
    suffixIcon: { type: String, default: "" },
    suffixTooltip: { type: String, default: "" },
    showErrorIcon: { type: Boolean, default: true },
    hasTooltip: { type: Boolean, default: false },
    tooltipText: { type: String, default: "" },
});
const emit = defineEmits(["update:modelValue"]);

const editor = ref(null);
const isEditable = ref(true);
const showMenu = ref(true);

// inline link ui state
const showLinkInput = ref(false);
const linkUrl = ref("");

const hasError = computed(() => props.errors && props.errors.$error);

watch(
    () => props.modelValue,
    (value) => {
        if (value !== editor.value?.getHTML()) {
            editor.value?.commands.setContent(value || "", false);
        }
    }
);

editor.value = new Editor({
    extensions: [
        StarterKit.configure({
            paragraph: true,
            bold: true,
            italic: true,
            bulletList: true,
            orderedList: true,
        }),
        Underline,
        Link.configure({
            openOnClick: false,
            autolink: true,
            linkOnPaste: true,
            protocols: ["http", "https", "mailto", "tel"],
        }),
    ],
    content: props.modelValue || "",
    onUpdate: ({ editor }) => {
        emit("update:modelValue", editor.getHTML());
    },
    editable: isEditable.value,
});

// open inline link input
const toggleLinkInput = () => {
    const previousUrl = editor.value?.getAttributes("link")?.href || "";
    linkUrl.value = previousUrl;
    showLinkInput.value = !showLinkInput.value;
};

// apply link from inline input
const applyLink = () => {
    const url = (linkUrl.value || "").trim();
    if (url === "") {
        editor.value.chain().focus().extendMarkRange("link").unsetLink().run();
    } else {
        editor.value
            .chain()
            .focus()
            .extendMarkRange("link")
            .setLink({ href: url })
            .run();
    }
    showLinkInput.value = false;
};

// remove link quickly
const removeLink = () => {
    editor.value.chain().focus().extendMarkRange("link").unsetLink().run();
    showLinkInput.value = false;
};

watch(isEditable, (value) => {
    editor.value?.setEditable(value);
});

onBeforeUnmount(() => {
    editor.value?.destroy();
});
</script>

<template>
    <div class="form-group mb-1">
        <label v-if="$attrs.label" :for="$attrs.id" class="form-label">
            {{ $attrs.label }}
            <span v-if="required" class="text-black astric">*</span>
        </label>

        <div class="input-with-icon" :class="{ 'has-error': hasError }">
            <!-- BubbleMenu -->
            <BubbleMenu
                v-if="editor && showMenu"
                :editor="editor"
                :options="{ placement: 'top-start', offset: [0, 4], appendTo: () => document.body, zIndex: 9999 }"
            >
                <div class="bubble-menu">
                    <!-- Bold -->
                    <button
                        @click="editor.chain().focus().toggleBold().run()"
                        :class="{ 'is-active': editor.isActive('bold') }"
                        type="button"
                    >
                        <img
                            src="../../assets/img/bold-icon.png"
                            style="width: 32px; height: 32px"
                        />
                    </button>

                    <!-- Italic -->
                    <button
                        @click="editor.chain().focus().toggleItalic().run()"
                        :class="{ 'is-active': editor.isActive('italic') }"
                        type="button"
                    >
                        <img
                            src="../../assets/img/italic-icon.png"
                            style="width: 32px; height: 32px"
                        />
                    </button>

                    <!-- Underline -->
                    <button
                        @click="editor.chain().focus().toggleUnderline().run()"
                        :class="{ 'is-active': editor.isActive('underline') }"
                        type="button"
                    >
                        <img
                            src="../../assets/img/underline-icon.png"
                            style="width: 32px; height: 32px"
                        />
                    </button>

                    <!-- Paragraph -->
                    <button
                        @click="editor.chain().focus().setParagraph().run()"
                        :class="{ 'is-active': editor.isActive('paragraph') }"
                        type="button"
                    >
                        <img
                            src="../../assets/img/frame-icon.png"
                            style="width: 32px; height: 32px"
                        />
                    </button>

                    <!-- Link -->
                    <button
                        @click="toggleLinkInput"
                        :class="{ 'is-active': editor.isActive('link') }"
                        type="button"
                    >
                        <img
                            src="../../assets/img/link-icon.png"
                            style="width: 32px; height: 32px"
                        />
                    </button>

                    <!-- Inline link input -->
                    <div v-if="showLinkInput" class="link-input">
                        <input
                            v-model="linkUrl"
                            type="text"
                            placeholder="https://example.com"
                            @keyup.enter="applyLink"
                        />
                        <button type="button" class="apply" @click="applyLink">
                            Apply
                        </button>
                        <button
                            type="button"
                            class="remove"
                            @click="removeLink"
                        >
                            Remove
                        </button>
                    </div>

                    <!-- Bullet List -->
                    <button
                        @click="editor.chain().focus().toggleBulletList().run()"
                        :class="{ 'is-active': editor.isActive('bulletList') }"
                        type="button"
                    >
                        <img
                            src="../../assets/img/points-icon.png"
                            style="width: 32px; height: 32px"
                        />
                    </button>

                    <!-- Ordered List -->
                    <button
                        @click="
                            editor.chain().focus().toggleOrderedList().run()
                        "
                        :class="{ 'is-active': editor.isActive('orderedList') }"
                        type="button"
                    >
                        <img
                            src="../../assets/img/listing-icon.png"
                            style="width: 32px; height: 32px"
                        />
                    </button>
                </div>
            </BubbleMenu>

            <!-- The editor -->
            <EditorContent
                :editor="editor"
                class="form-control tiptap-editor"
            />

            <template v-if="hasTooltip">
                <TooltipIcon :content="tooltipText" />
            </template>

            <!-- <span
                v-if="hasError && showErrorIcon"
                class="suffix-icon error-icon"
                :title="errors?.$errors?.[0]?.$message || ''"
            >
                !
            </span>
            <span
                v-else-if="suffixIcon"
                class="suffix-icon"
                :title="suffixTooltip"
            >
                <i :class="suffixIcon"></i>
            </span> -->
        </div>

        <small
            v-if="errors.$error"
            class="text-danger"
            style="font-size: 0.8rem; top: 40px"
        >
            {{ errors.$errors[0].$message }}
        </small>
    </div>
</template>

<style scoped>
.input-with-icon {
    position: relative;
}

.form-control.tiptap-editor {
    border: 1px solid #ced4da;
    border-radius: 0.5rem;
    padding: 0.75rem;
    padding-bottom: 1.25rem; /* leave room for bubble at bottom */
    min-height: 120px;
    background: #fff;
    max-height: 250px; /* limit box height */
    overflow-y: auto; /* add scrollbar when too much text */
    word-wrap: break-word; /* wrap long words */
    white-space: pre-wrap; /* respect line breaks */
}

/* Ensure bold/italic render even if global CSS overrides */
.form-control.tiptap-editor :deep(strong) {
    font-weight: 700;
}
.form-control.tiptap-editor :deep(em) {
    font-style: italic;
}

.bubble-menu {
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    display: inline-flex;
    gap: 2px; /* tighter spacing */
    padding: 2px; /* tighter padding */
    align-items: center;
}

.bubble-menu button {
    background: transparent;
    border: none;
    padding: 4px; /* tighter button padding */
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0; /* remove extra inline spacing */
}

.bubble-menu button:hover {
    background: #f3f3f3;
}

.bubble-menu button.is-active {
    background: #e0e7ff;
    color: #4338ca;
}

.bubble-menu .icon {
    width: 18px;
    height: 18px;
}

.bubble-menu img {
    display: block; /* avoid extra pixels below images */
    width: 18px; /* consistent, compact icon size */
    height: 18px;
}

/* Inline link input styling */
.link-input {
    display: inline-flex;
    gap: 6px;
    align-items: center;
    margin-left: 4px;
}
.link-input input {
    width: 200px;
    padding: 6px 8px;
    border: 1px solid #ddd;
    border-radius: 4px;
    font-size: 0.9rem;
}
.link-input .apply {
    background: #4338ca;
    color: #fff;
    padding: 6px 10px;
    border-radius: 4px;
}
.link-input .remove {
    background: #f44336;
    color: #fff;
    padding: 6px 10px;
    border-radius: 4px;
}

/* Ensure bullets/numbers are visible without increasing overall editor padding */
.form-control.tiptap-editor :deep(ul) {
    list-style-type: disc;
    list-style-position: outside;
    margin: 0 0 0.5rem 1rem; /* slightly closer to edge */
    padding-left: 0.5rem; /* equal inner space for bullets/numbers */
}
.form-control.tiptap-editor :deep(ol) {
    list-style-type: decimal;
    list-style-position: outside;
    margin: 0 0 0.5rem 1rem;
    padding-left: 0.5rem; /* match ul for alignment */
}
.form-control.tiptap-editor :deep(li) {
    list-style: inherit;
}
</style>
